import { useEffect, useMemo, useRef } from "react";
import { models } from "powerbi-client";
import type { Report } from "powerbi-client";
import { EventHandler, PowerBIEmbed } from "powerbi-client-react";
import styles from "./PowerBIComponent.module.scss";

// How long before the known expiration time to proactively fetch a new token,
// so the swap happens before the user can ever hit a TokenExpired failure.
// Kept below PowerBiService.cs's TokenRefreshBufferMinutes (10 min) on purpose:
// that's the backend's floor on an embed token's remaining life when minted, so
// staying under it leaves real headroom instead of both buffers racing to zero.
const REFRESH_BUFFER_MS = 9 * 60 * 1000;

interface PowerBIComponentProps {
  embedUrl: string;
  accessToken: string;
  reportId: string;
  expiration: string;
  filters?: models.IReportEmbedConfiguration["filters"];
  cssClassName?: string;
  onTokenExpired: () => void;
}

/**
 * Presentational, read-only Power BI report embed. Receives an already-resolved
 * embed url, access token, and report id; performs no data fetching or sign-in.
 *
 * The installed powerbi-client SDK has no subscribable "tokenExpired" event
 * (see Report.allowedEvents in the SDK source); an expired token instead
 * surfaces as a generic "error" event with detail.errorCode === TokenExpired.
 * When that happens we ask the parent for a fresh token via onTokenExpired,
 * then reload the report once the new accessToken prop comes back, since
 * updating an already-expired token in place isn't enough on its own.
 */
const PowerBIComponent = ({
  embedUrl,
  accessToken,
  reportId,
  expiration,
  filters,
  cssClassName = "report-style-class",
  onTokenExpired,
}: PowerBIComponentProps) => {
  const reportRef = useRef<Report | undefined>(undefined);
  const pendingReloadRef = useRef(false);

  useEffect(() => {
    if (pendingReloadRef.current) {
      pendingReloadRef.current = false;
      reportRef.current?.reload();
    }
  }, [accessToken]);

  // Proactively swap the token before it actually expires. PowerBIEmbed's
  // componentDidUpdate calls report.setAccessToken() in place whenever the
  // accessToken prop changes (see embedOrUpdateAccessToken in
  // powerbi-client-react), so as long as we refresh ahead of the real
  // expiration this never surfaces a visible error or needs a reload.
  //
  // Only scheduled while the tab is visible: a backgrounded dashboard has no
  // user to keep imperceptible for, so there's no point spending a token
  // refresh on it. The timer is re-armed against the (unchanged) expiration
  // as soon as the tab becomes visible again, refreshing immediately if the
  // buffered deadline already passed while hidden.
  useEffect(() => {
    const expiresAt = new Date(expiration).getTime();
    if (Number.isNaN(expiresAt)) return undefined;

    let timerId: number | undefined;

    const scheduleIfVisible = () => {
      if (document.visibilityState !== "visible") return;
      const delay = Math.max(expiresAt - Date.now() - REFRESH_BUFFER_MS, 0);
      timerId = window.setTimeout(onTokenExpired, delay);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        scheduleIfVisible();
      } else if (timerId !== undefined) {
        window.clearTimeout(timerId);
        timerId = undefined;
      }
    };

    scheduleIfVisible();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (timerId !== undefined) window.clearTimeout(timerId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [expiration, onTokenExpired]);

  const eventHandlersMap = useMemo(
    () =>
      new Map<string, EventHandler>([
        ["loaded", () => console.log("Power BI report loaded")],
        ["rendered", () => console.log("Power BI report rendered")],
        [
          "error",
          (event) => {
            if (!event) return;
            console.error(event.detail);
            if (event.detail?.message === models.CommonErrorCodes.TokenExpired) {
              pendingReloadRef.current = true;

              onTokenExpired();
            }
          },
        ],
      ]),
    [onTokenExpired]
  );

  const embedConfig: models.IReportEmbedConfiguration = {
    type: "report",
    id: reportId,
    embedUrl,
    accessToken,
    tokenType: models.TokenType.Embed,
    filters,
    settings: {
      filterPaneEnabled: false,
      navContentPaneEnabled: true,
      layoutType: models.LayoutType.Custom,
      customLayout: {
        displayOption: models.DisplayOption.FitToWidth,
      },
    },
  };

  return (
    <div className={styles.reportContainer} data-testid="powerbi-report-container">
      <PowerBIEmbed
        embedConfig={embedConfig}
        eventHandlers={eventHandlersMap}
        cssClassName={cssClassName}
        getEmbeddedComponent={(embedObject) => {
          reportRef.current = embedObject as Report;
        }}
      />
    </div>
  );
};

export default PowerBIComponent;
